import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowRight, Zap, Shield, Globe, MessageSquare, TrendingDown, Clock, CheckCircle, X, Play, ShieldCheck, Lock, Mic } from 'lucide-react';
import Footer from '@/components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Video URL for Learn More - using local file for better compatibility
const PROMO_VIDEO_URL = "/promo-video.mp4";

const LandingPage = ({ onLogin }) => {
  const [showAuth, setShowAuth] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    const formData = new FormData(e.target);
    const data = {
      email: formData.get('email'),
      password: formData.get('password'),
      full_name: formData.get('full_name')
    };
    
    try {
      const response = await axios.post(`${API}/auth/register`, data);
      toast.success('Account created successfully!');
      
      // Auto login after registration
      const loginResponse = await axios.post(`${API}/auth/login`, {
        email: data.email,
        password: data.password
      });
      
      onLogin(loginResponse.data.user, loginResponse.data.token);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };
  
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    const formData = new FormData(e.target);
    const data = {
      email: formData.get('email'),
      password: formData.get('password')
    };
    
    try {
      const response = await axios.post(`${API}/auth/login`, data);
      toast.success('Welcome back!');
      onLogin(response.data.user, response.data.token);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  if (showAuth) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-2xl border-0">
          <CardHeader className="space-y-1">
            <div className="flex items-center justify-center mb-4">
              <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg">
                <Globe className="w-8 h-8 text-white" />
              </div>
            </div>
            <CardTitle className="text-2xl text-center font-bold">Welcome to RouteFlow AI</CardTitle>
            <CardDescription className="text-center">Create an account or sign in to start transferring</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="login" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="login" data-testid="login-tab">Login</TabsTrigger>
                <TabsTrigger value="register" data-testid="register-tab">Register</TabsTrigger>
              </TabsList>
              
              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email</Label>
                    <Input id="login-email" name="email" type="email" placeholder="you@example.com" required data-testid="login-email-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password">Password</Label>
                    <Input id="login-password" name="password" type="password" required data-testid="login-password-input" />
                  </div>
                  <Button type="submit" className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700" disabled={loading} data-testid="login-submit-btn">
                    {loading ? 'Signing in...' : 'Sign In'}
                  </Button>
                  <div className="text-center mt-3">
                    <a href="/forgot-password" className="text-sm text-emerald-600 hover:text-emerald-700 underline">
                      Forgot your password?
                    </a>
                  </div>
                </form>
              </TabsContent>
              
              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="register-name">Full Name</Label>
                    <Input id="register-name" name="full_name" placeholder="John Doe" required data-testid="register-name-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-email">Email</Label>
                    <Input id="register-email" name="email" type="email" placeholder="you@example.com" required data-testid="register-email-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-password">Password</Label>
                    <Input id="register-password" name="password" type="password" required data-testid="register-password-input" />
                  </div>
                  <div className="flex items-start space-x-2 pt-2">
                    <input 
                      type="checkbox" 
                      id="terms-checkbox" 
                      name="terms_accepted"
                      required 
                      className="mt-1 h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                      data-testid="terms-checkbox"
                    />
                    <Label htmlFor="terms-checkbox" className="text-sm text-gray-600 leading-tight">
                      I agree to the <a href="/terms" target="_blank" className="text-emerald-600 hover:underline font-medium">Terms of Service</a> and <a href="/privacy" target="_blank" className="text-emerald-600 hover:underline font-medium">Privacy Policy</a>. I understand that RouteFlow AI is a technology interface only and all financial services are provided by our licensed financial partner.
                    </Label>
                  </div>
                  <Button type="submit" className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700" disabled={loading} data-testid="register-submit-btn">
                    {loading ? 'Creating account...' : 'Create Account'}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
            
            <Button variant="ghost" className="w-full mt-4" onClick={() => setShowAuth(false)} data-testid="back-to-landing-btn">
              Back to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 glass-effect border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_smart-money-route/artifacts/6teoyzj7_Generated%20Image%20November%2018%2C%202025%20-%203_06PM.png" 
                alt="RouteFlow AI" 
                className="h-10 w-auto"
              />
              <span className="text-xl font-bold gradient-text">RouteFlow AI</span>
            </div>
            <div className="flex items-center space-x-4">
              <a href="#features" className="text-gray-700 hover:text-emerald-600 smooth-transition hidden sm:inline-block">Features</a>
              <a href="#how-it-works" className="text-gray-700 hover:text-emerald-600 smooth-transition hidden sm:inline-block">How It Works</a>
              <Button onClick={() => setShowAuth(true)} className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700" data-testid="get-started-btn">
                Get Started <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 opacity-90"></div>
          <img 
            src="https://images.unsplash.com/photo-1758522484797-49f88e04809c?w=1600&q=80" 
            alt="Digital payments" 
            className="absolute right-0 top-0 w-1/2 h-full object-cover opacity-20"
          />
        </div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Send Money Globally with
                <span className="text-emerald-500"> AI-Powered Routing & Voice Assistant</span>
              </h1>
              <p className="text-base sm:text-lg text-gray-600 mb-8">
                Your intelligent financial GPS. Tell us where to send money, and our AI finds the fastest, cheapest route across blockchain networks.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button size="lg" onClick={() => setShowAuth(true)} className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-lg px-8" data-testid="hero-get-started-btn">
                  Start Sending Money <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button size="lg" variant="outline" onClick={() => setShowVideoModal(true)} className="border-2 border-emerald-500 text-emerald-600 hover:bg-emerald-50 text-lg px-8" data-testid="learn-more-btn">
                  <Play className="mr-2 w-5 h-5" /> Learn More
                </Button>
              </div>
              
              {/* Stats */}
              <div className="grid grid-cols-3 gap-6 mt-12">
                <div>
                  <p className="text-3xl font-bold text-emerald-600">44</p>
                  <p className="text-sm text-gray-600">Fiat Currencies</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-emerald-600">121</p>
                  <p className="text-sm text-gray-600">Countries</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-emerald-600">12</p>
                  <p className="text-sm text-gray-600">Languages</p>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1758273705998-05655eea4635?w=800&q=80" 
                alt="Happy customer using RouteFlow AI" 
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-xl shadow-xl">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Transfer Complete!</p>
                    <p className="text-sm text-gray-600">$500 → Mexico in 18 min</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technology Provider Banner - Option B */}
      <section className="bg-gradient-to-r from-emerald-600 to-teal-600 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 text-white">
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-6 h-6" />
              <span className="font-semibold">Technology Platform</span>
            </div>
            <span className="hidden sm:inline text-emerald-200">•</span>
            <div className="flex items-center gap-2">
              <Lock className="w-5 h-5" />
              <span className="text-sm sm:text-base">We never hold your funds - All transactions processed by licensed partners</span>
            </div>
          </div>
        </div>
      </section>

      {/* Key Differentiators Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-900 to-gray-800">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              What Makes Us Different
            </h2>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              The world's first AI-powered settlement platform with voice commands in 12 languages
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {/* Voice AI Feature - Emerald/Green */}
            <div className="bg-gradient-to-br from-emerald-500/20 to-green-500/20 rounded-2xl p-6 border border-emerald-500/30 hover:border-emerald-400 transition-all h-full">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl flex items-center justify-center shrink-0">
                  <Mic className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Voice AI Assistant</h3>
                  <p className="text-emerald-400 text-sm font-medium">12 Languages</p>
                </div>
              </div>
              <p className="text-gray-300 text-sm mb-4">
                Speak your commands. Send money, check balances, and create transfers using voice in your native language.
              </p>
              <div className="flex flex-wrap gap-1.5">
                {['EN', 'ES', 'FR', '中文', 'AR', 'HI'].map((lang) => (
                  <span key={lang} className="px-2 py-0.5 bg-emerald-500/20 text-emerald-300 rounded-full text-xs border border-emerald-500/30">
                    {lang}
                  </span>
                ))}
                <span className="px-2 py-0.5 bg-gray-700 text-gray-300 rounded-full text-xs">
                  +6
                </span>
              </div>
            </div>

            {/* Blockchain Routing Feature - Blue/Indigo */}
            <div className="bg-gradient-to-br from-blue-500/20 to-indigo-500/20 rounded-2xl p-6 border border-blue-500/30 hover:border-blue-400 transition-all h-full">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shrink-0">
                  <Zap className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">AI-Powered Routing</h3>
                  <p className="text-blue-400 text-sm font-medium">Blockchain Settlement</p>
                </div>
              </div>
              <p className="text-gray-300 text-sm mb-4">
                Our AI finds the fastest, cheapest path across networks. Settle in minutes with stablecoin rails.
              </p>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="flex items-center gap-1.5 text-gray-300">
                  <CheckCircle className="w-3.5 h-3.5 text-blue-400" />
                  <span>Instant Settlement</span>
                </div>
                <div className="flex items-center gap-1.5 text-gray-300">
                  <CheckCircle className="w-3.5 h-3.5 text-blue-400" />
                  <span>Non-Custodial</span>
                </div>
                <div className="flex items-center gap-1.5 text-gray-300">
                  <CheckCircle className="w-3.5 h-3.5 text-blue-400" />
                  <span>Smart Routing</span>
                </div>
                <div className="flex items-center gap-1.5 text-gray-300">
                  <CheckCircle className="w-3.5 h-3.5 text-blue-400" />
                  <span>USDC/Stablecoins</span>
                </div>
              </div>
            </div>

            {/* Flexible Cash-Out Options - Purple/Violet */}
            <div className="bg-gradient-to-br from-purple-500/20 to-violet-500/20 rounded-2xl p-6 border border-purple-500/30 hover:border-purple-400 transition-all h-full">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-violet-600 rounded-xl flex items-center justify-center shrink-0">
                  <Globe className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Flexible Cash-Out</h3>
                  <p className="text-purple-400 text-sm font-medium">Recipients Choose</p>
                </div>
              </div>
              <p className="text-gray-300 text-sm mb-4">
                Recipients pick what works best in their country - instant withdrawal, wallet transfer, or trusted exchanges.
              </p>
              <div className="space-y-2">
                <div className="flex items-center justify-between bg-white/5 rounded-lg px-3 py-2 border border-purple-500/20">
                  <span className="text-white text-sm font-medium">Instant Withdrawal</span>
                  <span className="text-xs bg-purple-500/30 text-purple-300 px-2 py-0.5 rounded-full">Soon</span>
                </div>
                <div className="flex items-center justify-between bg-white/5 rounded-lg px-3 py-2 border border-emerald-500/20">
                  <span className="text-white text-sm font-medium">Send to My Wallet</span>
                  <span className="text-xs bg-emerald-500/30 text-emerald-300 px-2 py-0.5 rounded-full">Live</span>
                </div>
                <div className="flex items-center justify-between bg-white/5 rounded-lg px-3 py-2 border border-emerald-500/20">
                  <span className="text-white text-sm font-medium">Use Any Exchange</span>
                  <span className="text-xs bg-emerald-500/30 text-emerald-300 px-2 py-0.5 rounded-full">Live</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Choose Your Service Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Choose Your Transfer Method
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Whether you prefer traditional fiat transfers or modern crypto solutions, we've got you covered
            </p>
          </div>
          
          <div className="max-w-3xl mx-auto">
            {/* Fiat Transfer Option */}
            <Card className="border-2 border-emerald-500 hover:shadow-xl group">
              <CardHeader>
                <div className="flex items-center justify-between mb-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                    <Globe className="w-8 h-8 text-white" />
                  </div>
                  <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-semibold">
                    Beta
                  </span>
                </div>
                <CardTitle className="text-2xl">International Money Transfer</CardTitle>
                <CardDescription className="text-base">
                  Fast, low-cost transfers powered by blockchain technology
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <p className="text-gray-700">Send USD, EUR, GBP, and 44 currencies</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <p className="text-gray-700">Card payments, bank transfers supported</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <p className="text-gray-700">Only 0.48% RouteFlow fee + provider fees</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <p className="text-gray-700">Transfers arrive in minutes, not days</p>
                  </div>
                </div>
                
                <div className="pt-4 border-t">
                  <Button 
                    onClick={() => setShowAuth(true)}
                    className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700" 
                  >
                    Start Sending Money <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Trust Banner */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white border-y">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
            <div className="flex items-center space-x-3">
              <Shield className="w-8 h-8 text-emerald-600" />
              <div>
                <p className="font-semibold text-gray-900">Bank-Level Security</p>
                <p className="text-sm text-gray-600">256-bit encryption</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Zap className="w-8 h-8 text-blue-600" />
              <div>
                <p className="font-semibold text-gray-900">Lightning Fast</p>
                <p className="text-sm text-gray-600">15-minute transfers</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <TrendingDown className="w-8 h-8 text-amber-600" />
              <div>
                <p className="font-semibold text-gray-900">Ultra-Low Fees</p>
                <p className="text-sm text-gray-600">From $1.20 per transfer</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4">Why Choose RouteFlow AI?</h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">Smart technology that makes international transfers simple, fast, and affordable</p>
          
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-0 shadow-lg hover:shadow-xl smooth-transition" data-testid="feature-card-ai">
              <CardHeader>
                <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center mb-4">
                  <MessageSquare className="w-6 h-6 text-white" />
                </div>
                <CardTitle>AI Chat Interface</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">Simply tell our AI where you want to send money. Natural conversation, no complex forms.</p>
              </CardContent>
            </Card>
            
            <Card className="border-0 shadow-lg hover:shadow-xl smooth-transition" data-testid="feature-card-speed">
              <CardHeader>
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center mb-4">
                  <Zap className="w-6 h-6 text-white" />
                </div>
                <CardTitle>Lightning Fast</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">Transfers arrive in minutes, not days. Blockchain technology ensures rapid delivery.</p>
              </CardContent>
            </Card>
            
            <Card className="border-0 shadow-lg hover:shadow-xl smooth-transition" data-testid="feature-card-fees">
              <CardHeader>
                <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center mb-4">
                  <TrendingDown className="w-6 h-6 text-white" />
                </div>
                <CardTitle>Ultra-Low Fees</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">Save up to 90% compared to traditional services. Fees from just $1.20 per transfer.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4">How It Works</h2>
          <p className="text-center text-gray-600 mb-16 max-w-2xl mx-auto">Three simple steps to send money anywhere in the world</p>
          
          <div className="space-y-20">
            {/* Step 1 */}
            <div className="grid md:grid-cols-2 gap-12 items-center" data-testid="step-1">
              <div className="order-2 md:order-1">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center text-white text-2xl font-bold flex-shrink-0">
                    1
                  </div>
                  <h3 className="text-2xl font-bold">Chat with AI Assistant</h3>
                </div>
                <p className="text-lg text-gray-600 mb-6">
                  Simply tell our AI where you want to send money. No complex forms or confusing options. Just natural conversation like texting a friend.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                    <span>Natural language processing</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                    <span>Instant route calculations</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                    <span>24/7 availability</span>
                  </li>
                </ul>
              </div>
              <div className="order-1 md:order-2">
                <img 
                  src="https://images.unsplash.com/photo-1681826291722-70bd7e9e6fc3?w=800&q=80" 
                  alt="Mobile banking app" 
                  className="rounded-2xl shadow-2xl"
                />
              </div>
            </div>

            {/* Step 2 */}
            <div className="grid md:grid-cols-2 gap-12 items-center" data-testid="step-2">
              <div>
                <img 
                  src="https://images.unsplash.com/photo-1744473331990-0e5024953832?w=800&q=80" 
                  alt="Crypto blockchain technology" 
                  className="rounded-2xl shadow-2xl"
                />
              </div>
              <div>
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white text-2xl font-bold flex-shrink-0">
                    2
                  </div>
                  <h3 className="text-2xl font-bold">AI Finds Best Route</h3>
                </div>
                <p className="text-lg text-gray-600 mb-6">
                  Our AI analyzes blockchain networks in real-time, selecting the optimal route for speed and cost. We send routing instructions to our licensed partners who execute the transfer - <strong>we never touch your money</strong>.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    <span>Intelligent network selection (Polygon, TON, etc.)</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    <span>We route instructions, not funds</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    <span>Licensed partners handle all transactions</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    <span>Transparent fee breakdown</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Step 3 */}
            <div className="grid md:grid-cols-2 gap-12 items-center" data-testid="step-3">
              <div className="order-2 md:order-1">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-600 rounded-full flex items-center justify-center text-white text-2xl font-bold flex-shrink-0">
                    3
                  </div>
                  <h3 className="text-2xl font-bold">Money Delivered Fast</h3>
                </div>
                <p className="text-lg text-gray-600 mb-6">
                  Funds arrive in your recipient's bank account or mobile wallet within minutes. Track every step in real-time.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                    <span>15-30 minute delivery</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                    <span>Real-time tracking</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                    <span>Instant notifications</span>
                  </li>
                </ul>
              </div>
              <div className="order-1 md:order-2">
                <img 
                  src="https://images.unsplash.com/photo-1761587941453-bd1790225d52?w=800&q=80" 
                  alt="Financial success" 
                  className="rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Global Destinations Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white/50">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">Send Money to 200+ Countries</h2>
              <p className="text-lg text-gray-600 mb-6">
                RouteFlow AI connects you to recipients worldwide. Whether you're supporting family, paying suppliers, or sending gifts, we've got you covered.
              </p>
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                    <Globe className="w-5 h-5 text-emerald-600" />
                  </div>
                  <span className="font-semibold">200+ Countries</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <Clock className="w-5 h-5 text-blue-600" />
                  </div>
                  <span className="font-semibold">24/7 Service</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
                    <Shield className="w-5 h-5 text-amber-600" />
                  </div>
                  <span className="font-semibold">Bank-Level Security</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                    <Zap className="w-5 h-5 text-purple-600" />
                  </div>
                  <span className="font-semibold">Instant Transfers</span>
                </div>
              </div>
              <Button onClick={() => setShowAuth(true)} className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700" data-testid="destinations-cta-btn">
                Start Sending Now <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1758242867435-a99042434f71?w=800&q=80" 
                alt="Global connectivity" 
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-xl shadow-lg">
                <p className="text-sm text-gray-600 mb-1">Popular destinations</p>
                <div className="flex space-x-2">
                  <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-semibold">🇲🇽 Mexico</span>
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold">🇵🇭 Philippines</span>
                  <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-semibold">🇳🇬 Nigeria</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-emerald-500 to-teal-600">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4 text-white">What Our Customers Say</h2>
          <p className="text-center text-emerald-50 mb-12 max-w-2xl mx-auto">Trusted by thousands of users worldwide</p>
          
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-0 shadow-xl" data-testid="testimonial-1">
              <CardContent className="p-6">
                <div className="flex items-center mb-4">
                  <div className="flex space-x-1">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} className="w-5 h-5 text-amber-400 fill-current" viewBox="0 0 20 20">
                        <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                      </svg>
                    ))}
                  </div>
                </div>
                <p className="text-gray-700 mb-4">"RouteFlow AI saved me so much money! I used to pay $45 to send $500 to my family in Mexico. Now it's only $2.40 and arrives in minutes. The AI chat makes it incredibly easy."</p>
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center text-white font-bold">
                    MR
                  </div>
                  <div>
                    <p className="font-semibold">Maria Rodriguez</p>
                    <p className="text-sm text-gray-600">Los Angeles, USA</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-xl" data-testid="testimonial-2">
              <CardContent className="p-6">
                <div className="flex items-center mb-4">
                  <div className="flex space-x-1">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} className="w-5 h-5 text-amber-400 fill-current" viewBox="0 0 20 20">
                        <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                      </svg>
                    ))}
                  </div>
                </div>
                <p className="text-gray-700 mb-4">"As a freelancer, I need to pay contractors in the Philippines regularly. RouteFlow's AI finds the fastest routes every time. It's like having a financial expert in my pocket!"</p>
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold">
                    JS
                  </div>
                  <div>
                    <p className="font-semibold">James Smith</p>
                    <p className="text-sm text-gray-600">London, UK</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-xl" data-testid="testimonial-3">
              <CardContent className="p-6">
                <div className="flex items-center mb-4">
                  <div className="flex space-x-1">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} className="w-5 h-5 text-amber-400 fill-current" viewBox="0 0 20 20">
                        <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                      </svg>
                    ))}
                  </div>
                </div>
                <p className="text-gray-700 mb-4">"The transparency is amazing! I see exactly what I'm paying and when the money will arrive. No hidden fees, no surprises. This is the future of money transfers."</p>
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-600 rounded-full flex items-center justify-center text-white font-bold">
                    AO
                  </div>
                  <div>
                    <p className="font-semibold">Adebayo Okonkwo</p>
                    <p className="text-sm text-gray-600">Berlin, Germany</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Frequently Asked Questions</h2>
            <p className="text-gray-600">Everything you need to know about RouteFlow AI</p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="faq-1" className="border-0 shadow-lg rounded-lg px-6 bg-white" data-testid="faq-1">
              <AccordionTrigger className="text-left font-semibold">
                How does RouteFlow AI work?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600">
                RouteFlow AI uses artificial intelligence to analyze thousands of transfer routes across blockchain networks. When you request a transfer, our AI instantly calculates the best options based on speed, cost, and reliability, then executes your chosen route automatically.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="faq-2" className="border-0 shadow-lg rounded-lg px-6 bg-white" data-testid="faq-2">
              <AccordionTrigger className="text-left font-semibold">
                Is my money safe with RouteFlow AI?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600">
                Absolutely! RouteFlow AI is a non-custodial platform, meaning you maintain full control of your funds at all times. We never hold your private keys or have access to your money. All transfers are secured by blockchain technology with bank-level encryption.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="faq-3" className="border-0 shadow-lg rounded-lg px-6 bg-white" data-testid="faq-3">
              <AccordionTrigger className="text-left font-semibold">
                How long do transfers take?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600">
                Transfer times vary by route and destination. Our fastest routes (using Polygon) take 15-30 minutes, balanced routes take 30-45 minutes, and our cheapest routes take 1-2 hours. The AI shows you exact timing before you confirm any transfer.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="faq-4" className="border-0 shadow-lg rounded-lg px-6 bg-white" data-testid="faq-4">
              <AccordionTrigger className="text-left font-semibold">
                What are the fees?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600">
                RouteFlow AI charges a transparent 0.48% service fee. Additionally, our payment partner charges processing fees which vary by payment method (typically 0.5-1.5%). You'll see the exact total fee before confirming any transfer - no hidden charges!
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="faq-5" className="border-0 shadow-lg rounded-lg px-6 bg-white" data-testid="faq-5">
              <AccordionTrigger className="text-left font-semibold">
                Which countries can I send money to?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600">
                RouteFlow AI supports transfers to 121 countries worldwide! We support 44 fiat currencies including USD, EUR, GBP, CAD, AUD, AED, INR, NGN, and many more. Our AI-powered routing finds the best path for your money regardless of destination.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="faq-6" className="border-0 shadow-lg rounded-lg px-6 bg-white" data-testid="faq-6">
              <AccordionTrigger className="text-left font-semibold">
                Do I need cryptocurrency knowledge to use RouteFlow AI?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600">
                Not at all! RouteFlow AI handles all the blockchain complexity behind the scenes. You simply tell our AI where you want to send money, and it takes care of everything - choosing the best blockchain, handling conversions, and ensuring delivery. It's as easy as sending a text message.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-emerald-500 to-teal-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Ready to Send Money Smarter?</h2>
          <p className="text-lg text-emerald-50 mb-8">Join thousands using AI-powered transfers to save time and money</p>
          <Button size="lg" onClick={() => setShowAuth(true)} className="bg-white text-emerald-600 hover:bg-gray-100 text-lg px-8" data-testid="cta-get-started-btn">
            Get Started Now <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <Footer />

      {/* Video Modal */}
      <Dialog open={showVideoModal} onOpenChange={setShowVideoModal}>
        <DialogContent className="sm:max-w-4xl p-0 overflow-hidden bg-black">
          <DialogHeader className="p-4 bg-gradient-to-r from-emerald-600 to-teal-600">
            <DialogTitle className="text-white flex items-center gap-2">
              <Play className="w-5 h-5" /> Discover RouteFlow AI
            </DialogTitle>
          </DialogHeader>
          <div className="relative w-full" style={{ paddingTop: '56.25%' }}>
            {showVideoModal && (
              <video
                className="absolute top-0 left-0 w-full h-full"
                controls
                playsInline
                poster="/video-poster.jpg"
              >
                <source src={PROMO_VIDEO_URL} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LandingPage;